import { PageResponse } from '../../player-setup-communication'
import PlayerSetupFormError from '../../player-setup-form-error/player-setup-form-error-tmpl'
import { PLAYER_ARRANGEMENT_OPTIONS, PLAYER_ARRANGEMENTS } from '../../player-setup-arrangement/player-setup-arrangement'
import { convertStandardToObjectResolution } from '../../player-data-builder'
import PlayerSetupCanvas from '../../player-setup-canvas/player-setup-canvas-tmpl'
import { assignDisplayPreviews } from '../player-setup-adjustments-preview'
import NumberMaskedInput from '../../../../../common/number-masked-input/number-masked-input-tmpl'
import { getCurrentLocale } from '@/i18n/i18ns';

const WIDTH = 'width'
const HEIGHT = 'height'

export const isOutOfBounds = (value, max) => {
  return value < 1 || value > max
}

export default {
  name: 'player-setup-led',
  components: {
    NumberMaskedInput,
    PlayerSetupCanvas,
    PlayerSetupFormError
  },
  props: {
    arrangementName: {
      type: String,
      default: ''
    },
    applicationType: {
      type: String,
      default: null
    },
    nextPage: {
      type: [String, Object, null],
      default: null
    },
    selectedResolution: {
      type: String,
      default: null
    },
    displayOrientation: {
      type: String,
      default: 'horizontal'
    },
    externalError: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      resolution: {
        width: 0,
        height: 0
      },
      outputs: [],
      arrangement: null,
      orientation: PLAYER_ARRANGEMENTS.horizontal,
      languageCode: navigator?.language ?? 'en',
      tableData: [],
      validationFailed: {
        width: false,
        height: false,
        outputs: []
      },
      hasErrors: false,
      totalResolution: { width: 0, height: 0 },

      // three important variables to manage canvas
      canvasName: '',
      canvasTag: null,
      canvasCheckInProgress: false
    }
  },
  mounted () {
    this.adjustAll()
  },
  watch: {
    arrangementName: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          const { width, height } = this.resolution
          const { outputs, arrangement, orientation } = PLAYER_ARRANGEMENT_OPTIONS[newValue]

          this.arrangement = arrangement ?? PLAYER_ARRANGEMENTS.x2x2
          this.orientation = orientation
          this.tableData = []
          this.validationFailed.outputs = []

          for (let i = 0; i < outputs; i++) {
            this.tableData.push({
              index: i,
              width: orientation === PLAYER_ARRANGEMENTS.horizontal ? width : height,
              height: orientation === PLAYER_ARRANGEMENTS.horizontal ? height : width
            })

            this.validationFailed.outputs.push({
              width: false,
              height: false
            })
          }

          this.tableData = assignDisplayPreviews(this.tableData, this.displayOrientation, this.arrangement)

          this.adjustAll()
        }
      }
    },
    selectedResolution: {
      immediate: true,
      handler (newValue) {
        if (newValue) {
          this.resolution = convertStandardToObjectResolution(newValue)
          const width = Number(this.resolution?.width ?? 0)
          const height = Number(this.resolution?.height ?? 0)

          this.tableData = this.tableData.map((output) => ({
            ...output,
            width: this.orientation === PLAYER_ARRANGEMENTS.horizontal ? width : height,
            height: this.orientation === PLAYER_ARRANGEMENTS.horizontal ? height : width
          }))
        }
      }
    },
    displayOrientation: {
      immediate: true,
      handler (newValue) {
        if (this.tableData.length > 0 && this.arrangement) {
          this.tableData = assignDisplayPreviews(this.tableData, newValue, this.arrangement)
        }
      }
    }
  },
  computed: {
    locale () {
      return getCurrentLocale();
    },
    maxWidth () {
      if (this.orientation === PLAYER_ARRANGEMENTS.horizontal) {
        return new Intl.NumberFormat(this.languageCode).format(this.resolution?.width ?? 0)
      } else {
        return new Intl.NumberFormat(this.languageCode).format(this.resolution?.height ?? 0)
      }
    },
    maxHeight () {
      if (this.orientation === PLAYER_ARRANGEMENTS.horizontal) {
        return new Intl.NumberFormat(this.languageCode).format(this.resolution?.height ?? 0)
      } else {
        return new Intl.NumberFormat(this.languageCode).format(this.resolution?.width ?? 0)
      }
    },
    outputBoxProperty () {
      return {
        'h-[112px] w-[63px]': /^vertial_(vertical|horizontal)_x[2-4]$/g.test(this.arrangementName) || this.arrangementName === 'vertial_x2x2',
        'h-[63px] w-[112px]': /^horizontal_(vertical|horizontal)_x[2-4]$/g.test(this.arrangementName) || this.arrangementName === 'horizontal_x2x2'
      }
    },
    gridProperty () {
      return {
        'grid-cols-1': /^(vertial|horizontal)_vertical_x[2-4]$/g.test(this.arrangementName),
        'grid-cols-2': /^(vertial|horizontal)_horizontal_x2$/g.test(this.arrangementName) || ['vertial_x2x2', 'horizontal_x2x2'].includes(this.arrangementName),
        'grid-cols-3': /^(vertial|horizontal)_horizontal_x3$/g.test(this.arrangementName),
        'grid-cols-4': /^(vertial|horizontal)_horizontal_x4$/g.test(this.arrangementName)
      }
    },
    sortedTableData () {
      const sortedTable = [...this.tableData].sort((a, b) => {
        if (a.label < b.label) return -1
        else if (a.label > b.label) return 1

        return 0
      })

      return sortedTable
    },
    isVVArrangement () {
      return /^vertial_vertical_x[2-4]$/g.test(this.arrangementName)
    }
  },
  methods: {
    adjustAll () {
      this.tableData.forEach((output, index) => {
        this.applyAdjustments(output.width, WIDTH, index)
        this.applyAdjustments(output.height, HEIGHT, index)
      })
    },
    resetValidation (row, column) {
      this.validationFailed[column] = false
      this.validationFailed.outputs[row][column] = false
    },
    validate (value, column, row) {
      this.resetValidation(row, column)

      const width = this.orientation === PLAYER_ARRANGEMENTS.horizontal ? this.resolution.width : this.resolution.height
      const height = this.orientation === PLAYER_ARRANGEMENTS.horizontal ? this.resolution.height : this.resolution.width

      this.validationFailed[column] = isOutOfBounds(value, column === WIDTH ? width : height)

      // validation failures for each cell
      this.validationFailed.outputs[row][column] = isOutOfBounds(value, column === WIDTH ? width : height)

      return this.validationFailed.outputs[row][column]
    },
    applyAdjustments (value, column, row) {
      if (this.arrangement === PLAYER_ARRANGEMENTS.horizontal) {
        // horizontal arrangement (1xN): all outputs should have the same height
        if (column === HEIGHT) {
          this.tableData = this.tableData.map((output) => ({
            ...output,
            height: value
          }))
        }

        this.totalResolution.height = this.tableData[0].height
        this.totalResolution.width = this.tableData.reduce(
          (sum, output) => sum + output.width,
          0
        )
      } else if (this.arrangement === PLAYER_ARRANGEMENTS.vertical) {
        // vertical arrangement (Nx1): all outputs should have the same width
        if (column === WIDTH) {
          this.tableData = this.tableData.map((output) => ({
            ...output,
            width: value
          }))
        }

        this.totalResolution.width = this.tableData[0].width
        this.totalResolution.height = this.tableData.reduce(
          (sum, output) => sum + output.height,
          0
        )
      } else if (this.arrangement === PLAYER_ARRANGEMENTS.x2x2) {
        if (column === HEIGHT) {
          // 2x2 arrangement: outputs 1&2, 3&4 should have same height
          if ([0, 1].includes(row)) {
            this.tableData[0].height = value
            this.tableData[1].height = value
          } else {
            this.tableData[2].height = value
            this.tableData[3].height = value
          }
        } else {
          // 2x2 arrangement: outputs 1&3, 2&4 should have same width
          if ([0, 2].includes(row)) {
            this.tableData[0].width = value
            this.tableData[2].width = value

            this.totalResolution.width = value
          } else {
            this.tableData[1].width = value
            this.tableData[3].width = value

            this.totalResolution.width = value
          }
        }

        this.totalResolution.height = this.tableData[0].height + this.tableData[2].height
        this.totalResolution.width = this.tableData[0].width + this.tableData[1].width
      }
    },
    /**
     * When any cell in the table gets changed,
     * do validation and apply necessary adjusments.
     * Adjustments will only be applied if there are no validation errors
     *
     * @param {WIDTH | HEIGHT} column
     * @param {number} row
     * @param {InputEvent} event
     */
    changeResolution (column, row, value) {
      this.tableData[row][column] = value

      this.hasErrors = this.validate(value, column, row)
      if (!this.hasErrors) {
        this.applyAdjustments(value, column, row)
      }
    },
    async submitForm () {
      if (!this.hasErrors && !this.canvasCheckInProgress) {
        const outputs = this.tableData

        return PageResponse(false, this.nextPage, {
          data: {
            outputs,
            totalResolution: {
              width: this.totalResolution.width,
              height: this.totalResolution.height
            },
            canvasName: this.canvasName,
            canvasTag: this.canvasTag
          }
        })
      }

      return PageResponse(true)
    },
    async triggerFormSubmit (event) {
      this.$emit('SubmitForm')
      return false
    },
    updateCanvasInfo (name, tag) {
      this.canvasName = name
      this.canvasTag = tag
    },
    async toggleCanvasCheckProgress (isInProgress) {
      this.canvasCheckInProgress = isInProgress
      await await this.$nextTick()
    }
  }
}
